import {cn} from '@/matic/lib/utils/cn';

type SegmentedControlOption<T extends string> = {
  value: T;
  label: string;
};

type SegmentedControlProps<T extends string> = {
  value: T;
  onChange: (value: T) => void;
  options: Array<SegmentedControlOption<T>>;
  className?: string;
  ariaLabel?: string;
  variant?: 'light' | 'dark';
};

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  className,
  ariaLabel,
  variant = 'light',
}: SegmentedControlProps<T>) {
  const isDark = variant === 'dark';
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex rounded-md p-0.5',
        isDark
          ? 'border border-white/10 bg-black/30'
          : 'border border-neutral-200 bg-neutral-50',
        className
      )}
    >
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            onClick={() => {
              if (!isActive) onChange(option.value);
            }}
            className={cn(
              'flex-1 rounded px-3 py-1 text-xs font-medium transition-colors',
              isDark
                ? isActive
                  ? 'bg-white/[0.12] text-white shadow-sm'
                  : 'text-sidebar-fg/70 hover:bg-white/5 hover:text-white'
                : isActive
                  ? 'bg-white text-neutral-900 shadow-sm'
                  : 'text-neutral-600 hover:text-neutral-900'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
